import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  CogIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ArrowLeftIcon,
  TableCellsIcon,
  DocumentMagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { loadExcelSettings, saveExcelSettings } from "../utils/excelSettings";

// Worked examples for the "What Check Doc looks for" panel. Each one is what Check Doc
// actually reports for that pair - keep them in step with courseDocCheck.js.
const TUTOR_EXAMPLES = [
  { id: "initials", columnE: "M. Tariq", part7: "Tariq", result: "OK", why: "Initials are dropped; the surname matches." },
  { id: "no-stop", columnE: "S Soroosh", part7: "Soroosh", result: "OK", why: "The full stop after an initial is optional." },
  { id: "case", columnE: "M. tariq", part7: "Tariq", result: "Mismatch", why: "Capitals must match." },
  { id: "other-tutor", columnE: "W Gunetilleke", part7: "Ali", result: "Mismatch", why: "A different tutor." },
  { id: "blank", columnE: "(blank)", part7: "Akter", result: "Mismatch", why: "Every course needs a tutor in column E." },
  { id: "joined", columnE: "D. DiEmidio", part7: "DiEmidio", result: "OK", why: "A two-word surname written as one word." },
  { id: "part7-two-words", columnE: "D. DiEmidio", part7: "Di Emidio", result: "Mismatch", why: "Segment 7 must be one word." },
  {
    id: "columnE-two-words",
    columnE: "D. Di Emidio",
    part7: "DiEmidio",
    result: "Ambiguous",
    why: "Column E’s surname is two words. Also a mismatch, since its last word is “Emidio”.",
  },
  {
    id: "two-tutors",
    columnE: "M. Tariq on one row, S. Tariq on another",
    part7: "Tariq",
    result: "Ambiguous",
    why: "One surname, two tutors: segment 7 cannot say which.",
  },
  {
    id: "no-initials",
    columnE: "Tariq on one row, M. Tariq on another",
    part7: "Tariq",
    result: "Ambiguous",
    why: "No initials counts as a different tutor.",
  },
];

// Written out in full so Tailwind keeps the classes; colours echo the report sections.
const TUTOR_RESULT_TONES = {
  OK: "text-gray-700",
  Mismatch: "text-rose-700",
  Ambiguous: "text-yellow-700",
};

const SettingsPage =({ onBack, roomSettings, onUpdateRoomSettings }) => {
  const [rooms, setRooms] = useState(roomSettings || []);
  const [editingRoom, setEditingRoom] = useState(null);
  /* eslint-disable no-unused-vars */
  const [editMode, setEditMode] = useState(false);
  /* eslint-enable no-unused-vars */
  const [newRoom, setNewRoom] = useState({ roomNumber: "", capacity: "" });
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("roomNumber");
  const [errors, setErrors] = useState({});
  const [headerChanges, setHeaderChanges] = useState(false);
  const [excelSettings, setExcelSettings] = useState(() => {
    const settings = loadExcelSettings();
    return (
      settings || {
        startRow: 4,
        columnHeaders: {
          A: "Item No.",
          B: "Unit No.",
          C: "VLE Class Name",
          D: "Unit Name",
          E: "Lecturer",
          F: "Day/Time",
          G: "EW/Time",
          H: "Group",
          I: "Cohort",
          J: "Room Allocated",
          K: "Students Coming",
          L: "Student Number",
        },
      }
    );
  });
  const [tempExcelSettings, setTempExcelSettings] = useState(() => {
    const settings = loadExcelSettings();
    return (
      settings || {
        startRow: 4,
        columnHeaders: {
          A: "Item No.",
          B: "Unit No.",
          C: "VLE Class Name",
          D: "Unit Name",
          E: "Lecturer",
          F: "Day/Time",
          G: "EW/Time",
          H: "Group",
          I: "Cohort",
          J: "Room Allocated",
          K: "Students Coming",
          L: "Student Number",
        },
      }
    );
  });
  const [excelErrors, setExcelErrors] = useState({});

  // Convert room settings format if needed
  useEffect(() => {
    if (roomSettings && Array.isArray(roomSettings)) {
      setRooms(roomSettings);
    } else if (roomSettings && typeof roomSettings === "object") {
      // Convert from object format to array format
      const roomArray = Object.entries(roomSettings).map(([roomNumber, capacity]) => ({
        roomNumber: roomNumber.toString(),
        capacity: parseInt(capacity),
        id: roomNumber,
      }));
      setRooms(roomArray);
    }
  }, [roomSettings]);

  // Ensure tempExcelSettings matches excelSettings
  useEffect(() => {
    setTempExcelSettings(excelSettings);
  }, [excelSettings]);

  const validateRoom = (room) => {
    const newErrors = {};

    if (!room.roomNumber.trim()) {
      newErrors.roomNumber = "Room number is required";
    } else if (!/^[a-zA-Z0-9]+$/.test(room.roomNumber.trim())) {
      newErrors.roomNumber = "Room number can only contain letters and numbers";
    }

    if (!room.capacity || room.capacity <= 0) {
      newErrors.capacity = "Capacity must be a positive number";
    } else if (room.capacity > 200) {
      newErrors.capacity = "Capacity cannot exceed 200";
    }

    return newErrors;
  };

  const handleAddRoom = () => {
    const trimmedRoom = {
      roomNumber: newRoom.roomNumber.trim(),
      capacity: parseInt(newRoom.capacity),
    };

    const validationErrors = validateRoom(trimmedRoom);

    // Check for duplicate room number
    if (rooms.some((room) => room.roomNumber === trimmedRoom.roomNumber)) {
      validationErrors.roomNumber = "Room number already exists";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const newRoomWithId = {
      ...trimmedRoom,
      id: trimmedRoom.roomNumber,
    };

    const updatedRooms = [...rooms, newRoomWithId].sort((a, b) => {
      if (sortBy === "roomNumber") {
        return a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true });
      }
      return a.capacity - b.capacity;
    });

    setRooms(updatedRooms);
    setNewRoom({ roomNumber: "", capacity: "" });
    setShowAddForm(false);
    setErrors({});

    // Update parent component
    onUpdateRoomSettings(updatedRooms);
  };

  const handleEditRoom = (room) => {
    setEditingRoom({ ...room });
    setErrors({});
  };

  const handleSaveEdit = () => {
    const validationErrors = validateRoom(editingRoom);

    // Check for duplicate room number (excluding current room)
    if (
      rooms.some((room) => room.id !== editingRoom.id && room.roomNumber === editingRoom.roomNumber)
    ) {
      validationErrors.roomNumber = "Room number already exists";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const updatedRooms = rooms
      .map((room) =>
        room.id === editingRoom.id
          ? { ...editingRoom, capacity: parseInt(editingRoom.capacity) }
          : room
      )
      .sort((a, b) => {
        if (sortBy === "roomNumber") {
          return a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true });
        }
        return a.capacity - b.capacity;
      });

    setRooms(updatedRooms);
    setEditingRoom(null);
    setErrors({});

    // Update parent component
    onUpdateRoomSettings(updatedRooms);
  };

  const handleDeleteRoom = (roomId) => {
    if (
      window.confirm("Are you sure you want to delete this room? This action cannot be undone.")
    ) {
      const updatedRooms = rooms.filter((room) => room.id !== roomId);
      setRooms(updatedRooms);
      onUpdateRoomSettings(updatedRooms);
    }
  };

  const handleCancelEdit = () => {
    setEditingRoom(null);
    setErrors({});
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewRoom({ roomNumber: "", capacity: "" });
    setErrors({});
  };

  const handleSaveExcelSettings = () => {
    saveExcelSettings(tempExcelSettings);
    setExcelSettings(tempExcelSettings);
    setHeaderChanges(false);
  };

  const handleCancelExcelSettings = () => {
    setTempExcelSettings(excelSettings);
    setHeaderChanges(false);
  };

  const filteredRooms = rooms
    .filter((room) => room.roomNumber.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "roomNumber") {
        return a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true });
      }
      return a.capacity - b.capacity;
    });

  const totalCapacity = rooms.reduce((sum, room) => sum + room.capacity, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - sticky, so Back to Main stays in view however far the page scrolls */}
      <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-300 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Main
              </button>
            </div>
            <div className="flex items-center">
              <CogIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h1 className="text-xl font-semibold text-gray-900">Room Settings</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Rooms</p>
                <p className="text-2xl font-bold text-gray-900">{rooms.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <UsersIcon className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Capacity</p>
                <p className="text-2xl font-bold text-gray-900">{totalCapacity}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg. Capacity</p>
                <p className="text-2xl font-bold text-gray-900">
                  {rooms.length > 0 ? Math.round(totalCapacity / rooms.length) : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 max-w-md">
                <label htmlFor="search" className="sr-only">
                  Search rooms
                </label>
                <input
                  type="text"
                  id="search"
                  placeholder="Search rooms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                />
              </div>
              <div className="flex items-center gap-4">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  <option value="roomNumber">Sort by Room Number</option>
                  <option value="capacity">Sort by Capacity</option>
                </select>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Room
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add Room Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Room</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="newRoomNumber"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Room Number
                  </label>
                  <input
                    type="text"
                    id="newRoomNumber"
                    value={newRoom.roomNumber}
                    onChange={(e) => setNewRoom({ ...newRoom, roomNumber: e.target.value })}
                    className={`block w-full rounded-md shadow-sm text-sm ${
                      errors.roomNumber
                        ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                    placeholder="e.g., 101, 203A"
                  />
                  {errors.roomNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.roomNumber}</p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="newCapacity"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Capacity
                  </label>
                  <input
                    type="number"
                    id="newCapacity"
                    value={newRoom.capacity}
                    onChange={(e) => setNewRoom({ ...newRoom, capacity: e.target.value })}
                    className={`block w-full rounded-md shadow-sm text-sm ${
                      errors.capacity
                        ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                    placeholder="e.g., 50"
                    min="1"
                    max="200"
                  />
                  {errors.capacity && (
                    <p className="mt-1 text-sm text-red-600">{errors.capacity}</p>
                  )}
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={handleCancelAdd}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Cancel
                </button>
                <button
                  onClick={handleAddRoom}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Add Room
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rooms Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Room Configuration ({filteredRooms.length} rooms)
            </h3>
          </div>
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Room Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Capacity
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRooms.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-12 text-center">
                        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No rooms found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {searchTerm
                            ? "Try adjusting your search."
                            : "Get started by adding a new room."}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredRooms.map((room) => (
                      <tr key={room.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingRoom?.id === room.id ? (
                            <input
                              type="text"
                              value={editingRoom.roomNumber}
                              onChange={(e) =>
                                setEditingRoom({ ...editingRoom, roomNumber: e.target.value })
                              }
                              className={`block w-24 rounded-md shadow-sm text-sm ${
                                errors.roomNumber
                                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                              }`}
                            />
                          ) : (
                            <div className="text-sm font-medium text-gray-900">
                              {room.roomNumber}
                            </div>
                          )}
                          {editingRoom?.id === room.id && errors.roomNumber && (
                            <p className="mt-1 text-xs text-red-600">{errors.roomNumber}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingRoom?.id === room.id ? (
                            <input
                              type="number"
                              value={editingRoom.capacity}
                              onChange={(e) =>
                                setEditingRoom({ ...editingRoom, capacity: e.target.value })
                              }
                              className={`block w-20 rounded-md shadow-sm text-sm ${
                                errors.capacity
                                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                              }`}
                              min="1"
                              max="200"
                            />
                          ) : (
                            <div className="text-sm text-gray-900">{room.capacity} students</div>
                          )}
                          {editingRoom?.id === room.id && errors.capacity && (
                            <p className="mt-1 text-xs text-red-600">{errors.capacity}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {editingRoom?.id === room.id ? (
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={handleSaveEdit}
                                className="text-green-600 hover:text-green-900 transition-colors duration-200"
                                title="Save changes"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
                                title="Cancel editing"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEditRoom(room)}
                                className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                                title="Edit room"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteRoom(room.id)}
                                className="text-red-600 hover:text-red-900 transition-colors duration-200"
                                title="Delete room"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Excel Settings Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <TableCellsIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Excel Settings</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-6">
                  <label
                    htmlFor="headerRow"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Column Header Row
                  </label>
                  <input
                    type="number"
                    id="headerRow"
                    value={excelSettings?.headerRow || 3}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (isNaN(value) || value < 1) {
                        setExcelErrors({
                          ...excelErrors,
                          headerRow: "Header row must be a positive number",
                        });
                      } else {
                        setExcelErrors({ ...excelErrors, headerRow: null });
                        const updatedSettings = { ...excelSettings, headerRow: value };
                        setExcelSettings(updatedSettings);
                        saveExcelSettings(updatedSettings);
                      }
                    }}
                    min="1"
                    className={`mt-1 block w-full rounded-md shadow-sm text-sm ${
                      excelErrors.headerRow
                        ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                  />
                  {excelErrors.headerRow && (
                    <p className="mt-1 text-sm text-red-600">{excelErrors.headerRow}</p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    This is the row containing your column headers in the Excel file.
                  </p>
                </div>

                <label htmlFor="startRow" className="block text-sm font-medium text-gray-700 mb-1">
                  Starting Row
                </label>
                <input
                  type="number"
                  id="startRow"
                  value={excelSettings?.startRow || 4}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (isNaN(value) || value < 1) {
                      setExcelErrors({
                        ...excelErrors,
                        startRow: "Starting row must be a positive number",
                      });
                    } else {
                      setExcelErrors({ ...excelErrors, startRow: null });
                      const updatedSettings = { ...excelSettings, startRow: value };
                      setExcelSettings(updatedSettings);
                      saveExcelSettings(updatedSettings);
                    }
                  }}
                  min="1"
                  className={`mt-1 block w-full rounded-md shadow-sm text-sm ${
                    excelErrors.startRow
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  }`}
                />
                {excelErrors.startRow && (
                  <p className="mt-1 text-sm text-red-600">{excelErrors.startRow}</p>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  This is the row where your actual data starts in the Excel file (must be after the
                  header row).
                </p>
                {/* Section header detection info */}
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-blue-800">Section header detection</h4>
                  <p className="mt-2 text-sm text-blue-700">
                    A row becomes a full-width section title instead of a data row when both of these
                    are true:
                  </p>
                  <ul className="mt-1 text-sm text-blue-700 list-disc list-inside space-y-1">
                    <li>Cell A holds the title text, and cells B and C are empty.</li>
                    <li>
                      The title contains one of “MSc”, “Sem”, “Foundation”,
                      “Year”, “Undergraduate”, “Postgraduate”,
                      “Diploma”, “Certificate”, “HND” or “BMS”
                      — or a 4-digit year in a title longer than 10 characters.
                    </li>
                  </ul>
                  <p className="mt-3 text-sm text-blue-700">
                    Two ways to get a valid section header row, both equally fine:
                  </p>
                  <ol className="mt-1 text-sm text-blue-700 list-decimal list-inside space-y-1">
                    <li>
                      <span className="font-medium">No merging at all.</span> Type the title in A.
                      Leave B and C blank. Done.
                    </li>
                    <li>
                      <span className="font-medium">Merge A–C (or A–L).</span> The merged
                      cell starts at A, so Excel stores the title in A and B/C end up with nothing in
                      them — the same state as option 1.
                    </li>
                  </ol>
                  <p className="mt-3 text-sm text-blue-700">
                    “Empty” means the cell has no value in it. It does not mean merged.
                    Putting the title in B, merging B–C on their own, or leaving a stray space in
                    B or C will all stop the row being treated as a section header.
                  </p>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">Column Headers</label>
                  {headerChanges && (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveExcelSettings}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        Save
                      </button>
                      <button
                        onClick={handleCancelExcelSettings}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <XMarkIcon className="h-4 w-4 mr-1" />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {excelSettings &&
                    excelSettings.columnHeaders &&
                    Object.entries(excelSettings.columnHeaders).map(([key, value]) => (
                      <div key={key} className="flex items-center">
                        <span className="text-sm text-gray-600 w-12">{key}:</span>
                        <input
                          type="text"
                          value={tempExcelSettings?.columnHeaders?.[key] || value || ""}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const newMapping = {
                              ...(tempExcelSettings.columnHeaders || {}),
                              [key]: newValue,
                            };
                            setTempExcelSettings({
                              ...tempExcelSettings,
                              columnHeaders: newMapping,
                            });
                            setHeaderChanges(true);
                          }}
                          className="ml-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          placeholder="Column Header"
                        />
                      </div>
                    ))}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Specify which Excel columns contain your data (A-L).
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Room allocation skip rules */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                When a row is skipped during allocation
              </h3>
            </div>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-700">
              “Auto Allocate Rooms” leaves Room Allocated (column J) empty for a row
              whenever any of the following is true:
            </p>
            <ul className="mt-3 text-sm text-gray-700 list-disc list-inside space-y-2">
              <li>The row is a section header.</li>
              <li>
                <span className="font-medium">Unit Name (D)</span> is empty.
              </li>
              <li>
                <span className="font-medium">Students Coming (K)</span> is empty, is 0, or is not a
                number (for example “TBC” or “N/A”).
              </li>
              <li>
                <span className="font-medium">Day/Time (F) and EW/Time (G)</span> together do not
                give both a day and a time period. The two columns are joined and anything in
                brackets is ignored. The day must be Mon, Tue, Wed, Thu, Fri, Sat or Sun in its
                three-letter form, so “Thursday AM” is not recognised. The period must be
                AM, PM or Eve, so “Mon Morning” and “Mon 9-12” are not recognised.
                Either column may supply either part, so an empty F with “Wed Eve” in G is
                fine.
              </li>
              <li>No room above has enough capacity for the student count.</li>
              <li>Every room big enough is already taken for that same day and time.</li>
            </ul>
            <p className="mt-4 text-sm text-gray-700">
              <span className="font-medium">Never checked:</span> Unit No. (B), VLE Class Name (C),
              Lecturer (E), Group (H) and Cohort (I). Leaving any of them blank does not stop a row
              from being allocated.
            </p>
            <p className="mt-2 text-sm text-gray-700">
              <span className="font-medium">Auto Allocate (Only Pending)</span> adds one rule: rows
              that already have a room are left untouched, and those rooms count as taken for their
              day and time.
            </p>
          </div>
        </div>


        {/* Check Doc rules */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <DocumentMagnifyingGlassIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                What “Check Doc” looks for
              </h3>
            </div>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-700">
              “Check Doc” reads the course full name in VLE Class Name (column C) and the tutor in
              Lecturer (column E), and reports anything that breaks the rules below. It never
              changes the file. A course full name is tilde-separated with eight parts and
              normally ends with a tilde:
            </p>
            <p className="mt-3 font-mono text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto">
              FDY001~135~414~Self As Learner~Feb26~EW~Gomwe~F26F~
            </p>
            <p className="mt-3 text-sm text-gray-700">
              Apart from blank spaces, parts 2, 3 and 6 (the two numbers and the mode) are never
              checked. The report groups its findings under these headings:
            </p>

            <ul className="mt-3 text-sm text-gray-700 list-disc list-inside space-y-2">
              <li>
                <span className="font-medium">Course name structure</span> — the name does not
                have exactly eight parts. Nothing else on that row is checked, because once the
                parts are out of step every other rule would report the wrong thing.
              </li>
              <li>
                <span className="font-medium">Blank spaces next to ~</span> — a space before or
                after any tilde, or at either end of the name, in any of the eight parts, such as
                “FDY005~ 136~” or “Communication  ~”. Every other rule checks the parts with
                these spaces removed, so a space is reported once, here.
              </li>
              <li>
                <span className="font-medium">Segment 1 — module code</span> — the module
                name in part 4 is known, but the code in part 1 is not the one recorded for it.
              </li>
              <li>
                <span className="font-medium">Segment 4 — module name</span> — the code is
                known but the name does not match it, or neither is known and the name is a near
                miss for a known one, which is reported as a probable spelling mistake.
              </li>
              <li>
                <span className="font-medium">Segment 5 — cohort</span> — not a month and a
                two-digit year, spelled out, such as “Feb26” or “Sep25”.
              </li>
              <li>
                <span className="font-medium">Cohort mismatch</span> — part 5 is not the month
                and year of the category heading above the course. A heading of “February 2024
                BA (Hons) - Level 5 - Sem 2” expects “Feb24”. The month and year can sit anywhere
                in the heading, the month may be written in full, as three letters or as
                “Sept”, and a heading such as “April/June 2023” accepts either month.
              </li>
              <li>
                <span className="font-medium">Segment 7 — tutor name mismatch</span> — part 7 is
                not the tutor’s surname from column E. Column E may put initials before the
                surname, with or without full stops, and the surname is its last word: “S.
                Soroosh”, “S Soroosh”, “M.Tariq” and “M. J. Hasan” are read as “Soroosh”, “Tariq”
                and “Hasan”. Part 7 must match exactly, capitals included, so “tariq” does not
                match “Tariq”. A surname is always one word, so part 7 “Di Emidio” is reported;
                write “DiEmidio”. Every course needs a tutor, so a blank part 7 or a blank column
                E is reported too.
              </li>
              <li>
                <span className="font-medium">Ambiguous tutor surname</span> — column E does not
                pin down one tutor, so part 7 cannot either. Each line lists every row involved
                for you to check; nothing is decided for you. There are two cases:
                <ul className="mt-2 ml-6 list-disc list-inside space-y-1">
                  <li>
                    <span className="font-medium">One surname, more than one tutor.</span> The same
                    surname appears anywhere in the sheet with different initials, such as “M.
                    Tariq” and “S. Tariq”. No initials counts as different, so “Tariq” and “M.
                    Tariq” are reported too. Full stops, spaces and capitals in the initials are
                    ignored, so “M. Tariq”, “M.Tariq” and “M Tariq” are one tutor. An
                    abbreviation such as “Sh.” counts as an initial, so “Sh. Aijaz” and “S. Aijaz”
                    are two tutors.
                  </li>
                  <li>
                    <span className="font-medium">A surname of more than one word.</span> Any
                    whole word before the surname makes it unclear: “D. Di Emidio” could mean
                    “Emidio” or “DiEmidio”, so write it “D. DiEmidio”. A full first name such as
                    “Enayet Sarwar”, or a placeholder such as “Teacher A”, is reported for the
                    same reason.
                  </li>
                </ul>
                <span className="block mt-2">
                  A row can appear under both tutor headings, because they are different problems.
                  This check reads column E alone, so a row that fails the structure check still
                  counts here.
                </span>
              </li>
              <li>
                <span className="font-medium">Segment 8 — session</span> — not a session
                code, a two-digit year and one group letter, such as “F26A”. The session
                code is short: J, F, A, JN, JL, S or N. Note that part 5 spells the month out while
                part 8 uses the code, so “Feb26” and “F26A” are both correct in
                their own place.
              </li>
              <li>
                <span className="font-medium">Duplicate groups</span> — one course appears more
                than once with the same group under the same heading. A course may run as groups A,
                B, C and so on, but the same group twice means a row has been duplicated. Rows are
                grouped by the heading above them within their department, so two headings with
                the same text count as one category only inside the same department. A department
                starts at a row beginning “Dept-”, such as “Dept- Business”, and runs
                until the next one.
              </li>
              <li>
                <span className="font-medium">Other</span> — anything that fits none of the
                above, so nothing is ever dropped from the report.
              </li>
            </ul>

            <p className="mt-4 text-sm font-medium text-gray-700">
              How segment 7 of the course full name is compared with column E
            </p>
            <p className="mt-1 text-sm text-gray-700">
              Segment 7 is the seventh tilde-separated part of the course full name in VLE Class
              Name (column C): the tutor’s surname, such as “Gomwe” in{" "}
              <span className="font-mono text-xs text-gray-800">
                FDY001~135~414~Self As Learner~Feb26~EW~
                <span className="font-semibold underline">Gomwe</span>~F26F~
              </span>
              .
            </p>
            <div className="mt-2 overflow-x-auto border border-gray-200 rounded">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Column E
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Segment 7 (course full name)
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reported as
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Why
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {TUTOR_EXAMPLES.map((example) => (
                    <tr key={example.id}>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800">{example.columnE}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800">{example.part7}</td>
                      <td className={`px-3 py-2 font-medium whitespace-nowrap ${TUTOR_RESULT_TONES[example.result]}`}>
                        {example.result}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{example.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-4 text-sm text-gray-700">
              <span className="font-medium">Rows that are not checked:</span> heading rows, blank
              rows, and rows whose VLE Class Name holds no tilde at all, such as
              “Students Support Class”. Rows whose module name is not in the course categories file
              are listed at the end of the report as skipped rather than counted as problems.
              So are rows under a heading that names no month and year, such as “DET”, whose
              cohort cannot be compared.
            </p>
            <p className="mt-2 text-sm text-gray-700">
              <span className="font-medium">“Fix Format”</span> trims blank spaces next to every
              tilde, and otherwise repairs only part 4, part 5 and part 8, and only when the
              correct value is certain. It never invents a group letter, and it leaves March, May,
              August, October and December sessions alone because those months have no session
              code. It never changes a cohort mismatch, because it cannot tell whether the course
              or the heading is wrong, and never touches part 7 or column E, so tutor problems are
              always yours to fix. Everything it cannot resolve is listed for you to fix by hand.
            </p>
            <p className="mt-2 text-sm text-gray-700">
              <span className="font-medium">“Fix Format” asks first, then saves.</span> Before it
              writes anything it shows a confirmation with how many course names it will change
              and how many room allocation or teacher name changes in the table are not saved in
              the file yet. Those unsaved changes are lost if you go ahead, because the table is
              reloaded from the file afterwards, so cancel and press “Save All Results into File”
              first to keep them. If there is nothing to fix, it says so and asks nothing.
            </p>
            <p className="mt-2 text-sm text-gray-700">
              Once you confirm, it writes its fixes straight into the workbook you opened, so
              there is no need to press “Save All Results into File” afterwards; that button
              saves rooms and teacher names, not course names. There is no undo, which is why the
              report lists every change it made. The browser may ask for permission to edit the
              file. If the file cannot be written, because it was dragged in rather than opened
              with Choose file or because it is open in Excel, nothing is changed and the app
              says why.
            </p>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CogIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Settings Note</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Changes to room and Excel settings will take effect immediately and will be used
                  for all future room allocations and conflict resolutions. Existing allocations
                  will not be automatically updated.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
